import React, { useState, useRef, useMemo } from 'react';
import {
  Pencil,
  Trash,
  Copy,
  Loader2,
  MoreVertical,
  FolderOpen,
  Users,
  LogOut,
  RotateCcw,
  AlertCircle
} from 'lucide-react';
import type { IProjectCardProps } from './ProjectCard.type';
import { ApplicationType } from './ProjectCard.type';
import { Text, MoreActions, Icon, Button } from '@ui-library';
import type { IMoreActionItem } from '../MoreActions/MoreActions.types';
import { IconName } from '../Icon/Icon.type';
import { ProjectRole, PlatformType } from '../../types/project';
import { getPlatformBadgeClass } from '../../config/platformConfig';
import { getInitials } from '../../utils/projectUtils';
import { useAuth } from '../../contexts/AuthContext';


export const ProjectCard: React.FC<IProjectCardProps> = ({
  projectType,
  projectName,
  clientName,
  dateDisplay,
  applicationType,
  projectId,
  onClick,
  onEdit,
  onDelete,
  onClone,
  onStats,
  cloneStatus,

  onRetryClone,
  onDeleteClone,
  isCloning = false,
  deleteLoading = false,
  className = "",
  onManageCollaborations,
  sharedBy,
  role,
  isInvitation = false,
  onAccept,
  onDecline,
  isAccepting = false,
  isDeclining = false,
  onLeave,
  isLeaving = false,
}: IProjectCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { hasAdminRole } = useAuth();

  let canAccessProject: boolean = true;
  if(isInvitation && !hasAdminRole) {
    canAccessProject = false;
  }
  // Disable access if clone is in progress or failed
  if (cloneStatus === 'cloning' || cloneStatus === 'failed') {
    canAccessProject = false;
  }

  const initials = getInitials(projectName);

  // Map ApplicationType to PlatformType for the central badge helper
  const applicationTypeToPlatform: Partial<Record<ApplicationType, PlatformType>> = {
    [ApplicationType.ServiceNow]: PlatformType.ServiceNow,
    [ApplicationType.PowerApps]: PlatformType.PowerApps,
    [ApplicationType.Salesforce]: PlatformType.Salesforce,
    [ApplicationType.Tyler]: PlatformType.TylerAppPlatform,
  };
  const badgeColorClass = getPlatformBadgeClass(
    applicationTypeToPlatform[applicationType],
    applicationType === ApplicationType.Web,
  );

  // Project initials disc uses the avatar-styles token pair, so it follows the
  // active theme instead of being pinned to a hardcoded hex.
  const avatarBgClass = "bg-avatar-styles-bg-neutral text-avatar-styles-fg-neutral";

  // Build menu items based on available actions
  const menuItems = useMemo((): IMoreActionItem[] => {
    const items: IMoreActionItem[] = [];

    // A collaborator (Editor role) on a shared project can manage it just like
    // the owner: clone, edit and manage collaborations. Owned projects (no
    // `sharedBy`) are always manageable. Viewers remain restricted.
    const canManageProject = !sharedBy || role === ProjectRole.EDITOR;

    // When clone is in progress or failed, show ONLY clone-related options
    if (cloneStatus === 'cloning' || cloneStatus === 'failed') {
      if (cloneStatus === 'failed' && onRetryClone && canManageProject) {
        items.push({
          id: 'retry-clone',
          label: 'Retry Clone',
          icon: <RotateCcw className="h-4 w-4" />,
          onClick: onRetryClone,
        });
      }

      if (onDeleteClone && canManageProject) {
        items.push({
          id: 'delete-clone',
          label: cloneStatus === 'cloning' ? 'Cancel Clone' : 'Delete Clone',
          icon: deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash className="h-4 w-4" />,
          onClick: onDeleteClone,
          variant: 'destructive',
          disabled: deleteLoading,
        });
      }

      return items;
    }

    // Normal menu items (when not cloning/failed)
    if (onClick) {
      items.push({
        id: 'open',
        label: 'Open Project',
        icon: <FolderOpen className="h-4 w-4" />,
        onClick: onClick,
      });
    }

    if (onEdit && canManageProject) {
      items.push({
        id: 'edit',
        label: 'Edit Project',
        icon: <Pencil className="h-4 w-4" />,
        onClick: onEdit,
      });
    }

    if (onClone && canManageProject) {
      items.push({
        id: 'clone',
        label: 'Clone Project',
        icon: isCloning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />,
        onClick: onClone,
        disabled: isCloning,
      });
    }

    if (onManageCollaborations && canManageProject) {
      items.push({
        id: 'manage-collaborations',
        label: 'Manage Collaborations',
        icon: <Users className="h-4 w-4" />,
        onClick: onManageCollaborations,
      });
    }

    if (onDelete && !sharedBy) {
      items.push({
        id: 'delete',
        label: 'Delete Project',
        icon: deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash className="h-4 w-4" />,
        onClick: onDelete,
        variant: 'destructive',
        disabled: deleteLoading,
      });
    }

    if (onLeave && sharedBy) {
      items.push({
        id: 'leave',
        label: 'Leave Project',
        icon: isLeaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />,
        onClick: onLeave,
        variant: 'destructive',
        disabled: isLeaving,
      });
    }

    return items;
  }, [onClick, onEdit, onClone, onDelete, onManageCollaborations, projectType, isCloning, deleteLoading, sharedBy, role, onLeave, isLeaving, cloneStatus, onRetryClone, onDeleteClone]);

  return (
    <div
      className={`
        bg-bg-primary
        rounded-bg-v2-xl
        container-shadow-sm
        border border-border-secondary
        p-bg-v2-lg
        flex
        flex-col
        gap-bg-v2-md
        ${canAccessProject ? 'cursor-pointer' : ''}
        ${(cloneStatus === 'cloning' || cloneStatus === 'failed') ? 'opacity-75' : ''}
        hover:container-shadow-md
        transition-all
        duration-200
        relative
        group
        w-full
        ${className}
      `}
      onClick={canAccessProject ? onClick : undefined}
    >
      {/* Main Content Row */}
      <div className="flex items-center gap-bg-v2-lg w-full relative">
        {/* Avatar */}
        <div
          className={`
            ${avatarBgClass}
            rounded-bg-v2-full
            w-bg-v2-8xl
            h-bg-v2-8xl
            flex
            items-center
            justify-center
            font-bg-v2-medium
            text-bg-v2-display-xs
            flex-shrink-0
          `}
        >
          {initials}
        </div>

        {/* Middle Section - Project Info */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-bg-v2-xxs">
          <div className="flex items-start justify-between gap-bg-v2-xxs">
            <Text variant="text-md" weight="medium" className="text-text-primary line-clamp-2 flex-1 max-w-[180px]">
              {projectName}
            </Text>
            {/* Application Type Badge */}
            <span className={`
              ${badgeColorClass}
              absolute
              top-0
              right-0
              px-bg-v2-md
              py-bg-v2-xxs
              rounded-bg-v2-full
              text-bg-v2-xs
              font-bg-v2-medium
              tracking-wide
              whitespace-nowrap
              shrink-0
            `}>
              {applicationType}
            </span>
          </div>

          {clientName && (
            <Text variant="text-sm" weight="medium" color="quaternary">
              {clientName}
            </Text>
          )}
          <div className="flex items-center justify-start gap-bg-v2-sm w-full">
            <Text variant="text-xs" color="quaternary">
              {cloneStatus === 'cloning' ? 'Cloning...' : dateDisplay}
            </Text>
          </div>
        </div>

        {/* Three Dot Menu + Clone Status Indicator */}
        {!isInvitation && (
          <div className="absolute bottom-0 right-0 flex items-center gap-1 p-1">
            {/* Status Indicator (Spinner for cloning, Error badge for failed) */}
            {cloneStatus === 'cloning' && (
              <div className="flex items-center justify-center" title="Cloning in progress...">
                <Loader2 className="h-5 w-5 animate-spin text-text-tertiary" />
              </div>
            )}

            {/* Failed state - clickable alert circle */}
            {cloneStatus === 'failed' && (
              <button
                ref={buttonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="flex items-center justify-center p-1 rounded-full hover:bg-bg-secondary-hover transition-all"
                title="Clone failed - click for options"
              >
                <AlertCircle className="h-5 w-5 text-red-500" />
              </button>
            )}

            {/* Menu Button - Hidden when clone failed */}
            {cloneStatus !== 'failed' && (
              <button
                ref={buttonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className={`
                  text-text-quaternary
                  hover:text-text-primary
                  cursor-pointer
                  transition-all
                  p-1
                  rounded-full
                  hover:bg-bg-secondary-hover
                  flex
                  items-center
                  justify-center
                  ${isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                  shrink-0
                `}
                title="More options"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* MoreActions Menu */}
      < MoreActions
        items={menuItems}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        position="bottom-right"
        triggerRef={buttonRef}
      />

      {/* Shared Project Info Section */}
      {
        (sharedBy || role) && (
          <>
            {/* Divider */}
            <div className="h-px w-full bg-border-secondary"></div>

            {/* Shared by and Role Section */}
            <div className="flex gap-bg-v2-xxs items-center w-full">
              {sharedBy && (
                <Text variant="text-sm" weight="medium" color="quaternary" className="flex-1">
                  Shared by{' '}
                  <span className="text-text-primary">{sharedBy}</span>
                </Text>
              )}
              {role && (
                <div className="bg-bg-primary border border-border-primary rounded-bg-v2-sm flex items-center justify-center px-bg-v2-md py-[3px] shrink-0">
                  <Text variant="text-xs" weight="medium" color="secondary">
                    {role}
                  </Text>
                </div>
              )}
            </div>
          </>
        )
      }

      {/* Invitation Actions Section */}
      {
        isInvitation && (onAccept || onDecline) && (
          <>
            {/* Divider */}
            <div className="h-px w-full bg-border-secondary"></div>

            {/* Accept and Decline Buttons */}
            <div className="flex items-center justify-between w-full gap-bg-v2-sm">
              {/* Accept Button */}
              {onAccept && (
                <div
                  className="relative shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    onClick={() => onAccept?.()}
                    disabled={isAccepting || isDeclining}
                    variant="primary"
                    size="small"
                    label={
                      <div className="flex gap-bg-v2-xs items-center">
                        <Icon name={IconName.Check} size="sm" inheritColor={true} />
                        <Text variant="text-sm" weight="semibold" color="on-brand">
                          Accept
                        </Text>
                      </div>
                    }
                    className="!rounded-bg-v2-md "
                  />
                  <div className="absolute inset-0 pointer-events-none shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] rounded-bg-v2-md" />
                </div>
              )}

              {/* Decline Button */}
              {onDecline && (
                <div
                  className="relative shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    onClick={() => onDecline?.()}
                    disabled={isAccepting || isDeclining}
                    variant="outline"
                    size="small"
                    label={
                      <Text variant="text-sm" weight="semibold" color="secondary">
                        Decline
                      </Text>
                    }
                    className="!rounded-bg-v2-md"
                  />
                  <div className="absolute inset-0 pointer-events-none shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] rounded-bg-v2-md" />
                </div>
              )}
            </div>
          </>
        )
      }

    </div >
  );
};
